import * as amplitude from "@amplitude/analytics-browser";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Box,
  Button,
  FormLabel,
  List,
  ListItemButton,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useContext, useState } from "react";

import AvatarWithStatus from "@/components/AvatarWithStatus";
import ElectronAppVersion from "@/components/electron/ElectronAppVersion.tsx";
import ElectronComponentLoader from "@/components/electron/ElectronComponentLoader.tsx";
import Loading from "@/components/Loading/Index";
import PortalDialog from "@/components/PortalDialog.tsx";
import UserFeedbackForm from "@/components/UserFeedbackForm.tsx";
import WorkspaceDropdown from "@/components/WorkspaceDropdown.tsx";
import { client } from "@/config";
import ModalForm from "@/elements/ModalForm";
import UseFirebase from "@/hooks/firebase/useFirebase";
import UseStorage from "@/hooks/useStorage";
import { AppContext } from "@/models/AppStateProvider";
import { enqueueCalculateUnreads, setUnreadAlsoSetAsReadOnNextBootstrap } from "@/models/actions/calculateUnreads";
import { forceReload } from "@/models/BootstrapUtils.ts";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider.tsx";
import { UxContext } from "@/models/UxStateProvider";
import { ipcRendererSend } from "@/utils";

export default function WorkspaceUserProfile({ newDesign }: { newDesign?: boolean }) {
  const { ampli } = useContext(TrackingContext);
  const { flags, setFlags } = React.useContext(AppContext);
  const { myAccount } = React.useContext(MyAccountContext);
  const { setUserFeedbackModalOpen, userFeedbackModalOpen } = React.useContext(UxContext);
  const { myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const [loggingOut, setLoggingOut] = React.useState<boolean>(false);
  const [reloading, setReloading] = React.useState<boolean>(false);
  const { showDebugMenu, allowLocalUserSettings } = flags;
  const { deleteActiveToken } = UseFirebase();
  const { clearLocalStorage } = UseStorage();
  const theme = useTheme();
  const { hideDriverMessages, setHideDriverMessages } = useContext(UxContext);
  const [teamDispatchConfirmOpen, setTeamDispatchConfirmOpen] = useState<boolean>(false);
  const [clearingDb, setClearingDb] = useState(false);

  const recordTeamDispatcherToggle = (enabling: boolean) => {
    const identifyEvent: amplitude.Identify = new amplitude.Identify();
    if (enabling) {
      ampli.toggleTeamDispatcherModeOn();
      identifyEvent.set("teamDispatcherMode", "on");
    } else {
      ampli.toggleTeamDispatcherModeOff();
      identifyEvent.set("teamDispatcherMode", "off");
    }
    amplitude.identify(identifyEvent);
  };

  const enableTeamDispatcherMode = () => {
    recordTeamDispatcherToggle(true);
    setHideDriverMessages(true);
    // dump db and do a full-bootstrap
    // await dbtruncate and bootstrap
    setClearingDb(true);
    if (myCurrentWorkspaceMembership) {
      setUnreadAlsoSetAsReadOnNextBootstrap();
    }
    void forceReload();
  };

  const disableTeamDispatcherMode = () => {
    recordTeamDispatcherToggle(false);
    setHideDriverMessages(false);
    if (myCurrentWorkspaceMembership) {
      enqueueCalculateUnreads({
        membership: myCurrentWorkspaceMembership,
        alsoSetAsRead: false,
      });
    }
  };

  const handleTeamDispatcherToggleClick = () => {
    if (hideDriverMessages) {
      disableTeamDispatcherMode();
    } else {
      setTeamDispatchConfirmOpen(true);
    }
  };

  const handleTeamDispatchConfirm = () => {
    setTeamDispatchConfirmOpen(false);
    enableTeamDispatcherMode();
  };

  const handleTeamDispatchCancel = () => {
    setTeamDispatchConfirmOpen(false);
  };

  const role = myCurrentWorkspaceMembership?.role;
  const showLocalUserSettingsMenu = allowLocalUserSettings && role === "member";

  const handleFlag = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFlags({ [event.target.name]: event.target.checked });
  };

  const handleClearCacheAndReload = useCallback(() => {
    setReloading(true);
    forceReload()
      .then(() => window.location.reload())
      .catch(() => window.location.reload());
  }, []);

  const handleSubmitFeedback = () => {
    setUserFeedbackModalOpen(true);
  };

  const { forceLogout, setForceLogout } = useContext(UxContext);

  const handleLogout = async () => {
    ampli.logoutManually();
    setForceLogout(() => true);
    ipcRendererSend("close-second-window");
    setLoggingOut(() => true);
    await deleteActiveToken();
    await forceReload();
    clearLocalStorage();
    return client.logout();
  };

  const readableFlags = Object.keys(flags)
    .filter((flag) => typeof flags[flag] !== "number")
    .map((flag) => ({
      key: flag,
      text: flag.replace(/([a-z])([A-Z])/g, "$1 $2"),
    }));

  const profileActions = [
    {
      text: "Submit Feedback",
      key: "submit-feedback",
      action: handleSubmitFeedback,
      icon: <KeyboardArrowRightIcon />,
    },
    {
      text: "Sign Out",
      key: "sign-out",
      action: handleLogout,
      icon: <ExitToAppIcon />,
    },
  ];

  const size = Notification?.permission === "granted" ? "275px" : "300px";
  const envValues = import.meta.env;

  return (
    <Box sx={{ ...(newDesign && { maxWidth: "896px", height: "100%" }) }}>
      <Box
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          p: newDesign ? 1 : 0,
          color: "#555",
          fontSize: "11px",
        }}
      >
        {envValues?.VITE_NEAREST_WEB_RELEASE_TAG && <span>{envValues.VITE_NEAREST_WEB_RELEASE_TAG}</span>}
      </Box>
      <ModalForm open={loggingOut} allowClose={false}>
        <Box sx={{ display: "flex" }}>
          <Loading variant="white" size="large" baseSize={6} /> <Box sx={{ ml: 2 }}>Signing Out</Box>
        </Box>
      </ModalForm>
      <Box sx={{ p: 2, pr: 0.75, position: "relative", height: "100%" }}>
        <Box sx={{ p: 2 }}>
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <AvatarWithStatus accountId={myAccount?.id} />
            <Typography sx={{ fontWeight: 500, fontSize: "1.25rem" }}>{myAccount?.name}</Typography>
          </Stack>
        </Box>

        {newDesign && (
          <Box sx={{ px: 2, pb: 1 }}>
            <WorkspaceDropdown />
          </Box>
        )}

        <List sx={{ p: 0 }} className="scrollable-content">
          {profileActions.map((item) => {
            if (item) {
              return (
                <ListItemButton
                  key={item.key}
                  aria-label={`Navigation to ${item.text}`}
                  onClick={item.action}
                  disableRipple={true}
                  sx={{
                    width: "100%",
                    borderRadius: "8px",
                    justifyContent: "space-between",
                    wordBreak: "break-word",
                    p: 2,
                    fontSize: "1.125rem",
                    fontWeight: 500,
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      textAlign: "left",
                      justifyContent: "start",
                    }}
                  >
                    {item.text}
                  </Box>
                  <Box
                    sx={{
                      flex: "0 1 auto",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {item.icon}
                  </Box>
                </ListItemButton>
              );
            }
          })}

          <ElectronComponentLoader component={<ElectronAppVersion />} />

          {showLocalUserSettingsMenu && (
            <Box sx={{ mt: 4 }}>
              <Box
                component="button"
                className="basic-button no-hover"
                sx={{
                  display: "flex",
                  textAlign: "left",
                  alignItems: "start",
                  gap: 2,
                  ml: 2,
                }}
                onClick={handleTeamDispatcherToggleClick}
              >
                <Box>
                  <Switch checked={hideDriverMessages} />
                </Box>

                <Box>
                  <Box>Team Dispatching Mode</Box>
                  <Box sx={{ fontSize: "14px", color: theme.palette.neutral.main }}>
                    When ON, messages from drivers will not show as unread if another dispatcher sees them before me.
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {showDebugMenu ? (
            <Box sx={{ p: 1, borderRadius: 1 }}>
              <Typography variant="h5" component="h2" fontWeight={700}>
                Debug Menu
              </Typography>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 1,
                  border: `1.5px solid ${theme.palette.secondary.light}`,
                  background: theme.palette.secondary.dark,
                }}
              >
                <FormLabel
                  sx={{
                    display: "flex",
                    cursor: "pointer",
                    alignItems: "center",
                  }}
                >
                  <Switch checked={Boolean(flags.newDesign)} onChange={handleFlag} name="newDesign" sx={{ mr: 2 }} />
                  <Typography fontWeight={600}>New Design</Typography>
                </FormLabel>
              </Box>
              <Box
                sx={{
                  overflowY: "auto",
                  maxHeight: "calc(100vh - 650px)",
                  p: 2,
                  mb: 2,
                  gap: 1,
                }}
              >
                {readableFlags.map((flag) => (
                  <FormLabel
                    sx={{
                      display: "flex",
                      cursor: "pointer",
                      alignItems: "center",
                      mb: 1,
                    }}
                    key={flag.key}
                  >
                    <Switch checked={Boolean(flags[flag.key])} onChange={handleFlag} name={flag.key} sx={{ mr: 2 }} />
                    <Box sx={{ textTransform: "capitalize" }}>{flag.text}</Box>
                  </FormLabel>
                ))}
              </Box>
              <Box sx={{ px: 2, mb: 2 }}>
                <FormLabel sx={{ display: "block", mb: 0.5 }}>Unread Debounce (ms)</FormLabel>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={flags.unreadDebounceMs ?? 2000}
                  onChange={(e) => {
                    const parsed = Number(e.target.value);
                    if (!Number.isNaN(parsed) && parsed >= 0) {
                      setFlags({ unreadDebounceMs: parsed });
                    }
                  }}
                  slotProps={{ htmlInput: { min: 0, step: 100 } }}
                />
              </Box>
              <Box>
                <Button variant="outlined" disabled={reloading} onClick={handleClearCacheAndReload} fullWidth>
                  {reloading ? "Reloading..." : "Clear Cache & Reload"}
                </Button>
              </Box>
            </Box>
          ) : null}
        </List>
      </Box>
      {newDesign && (
        <PortalDialog
          title="Leave Feedback"
          open={userFeedbackModalOpen}
          onClose={() => setUserFeedbackModalOpen(false)}
        >
          <UserFeedbackForm onClose={() => setUserFeedbackModalOpen(false)} />
        </PortalDialog>
      )}
      <PortalDialog
        title="Turn on Team Dispatching Mode?"
        open={teamDispatchConfirmOpen}
        onClose={handleTeamDispatchCancel}
      >
        <Stack sx={{ width: "100%", maxWidth: "500px", gap: 3 }}>
          <Typography sx={{ fontSize: "1rem" }}>
            Turning this on will resync your unreads. Unread indicators will be cleared where another dispatcher has
            already read the drivers message.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleTeamDispatchCancel}
              aria-label="Cancel turning on Team Dispatching Mode"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Cancel
            </Button>
            <Button
              disabled={clearingDb}
              variant="contained"
              color="primary"
              onClick={handleTeamDispatchConfirm}
              aria-label="Confirm turning on Team Dispatching Mode"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Turn On
            </Button>
          </Box>
        </Stack>
      </PortalDialog>
    </Box>
  );
}
